import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, X, AlertTriangle, ArrowRight } from "lucide-react";

import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// FIN-7 / H0-S3 — Modal de reconciliação de duplicatas.
//
// Renderiza candidatos {manual, pluggy, score} pra user decidir:
//   confirm_duplicate → mantém manual, deleta a tx Pluggy importada
//   reject_duplicate  → mantém ambas (não eram duplicatas mesmo)
//
// Score < 0.85 nunca chega aqui (filtrado server-side).
// Match exato (1.0) também não chega aqui — é resolvido sozinho.

const fmtBRL = (v) =>
  Number(v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function _fmtDate(s) {
  if (!s) return "—";
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString("pt-BR");
}

function CandidateRow({ candidate, onResolve, isResolving }) {
  const m = candidate?.payload?.manual;
  const p = candidate?.payload?.pluggy;
  const score = Math.round((candidate?.score || 0) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-xl border border-status-warning/30 bg-status-warning/5 p-4"
      data-testid={`reconcile-candidate-${candidate.id}`}
    >
      <div className="flex items-center justify-between mb-3">
        <Badge
          variant="outline"
          className="bg-status-warning/15 text-status-warning border-status-warning/40 text-caption"
        >
          <AlertTriangle className="w-3 h-3 mr-1" aria-hidden="true" />
          Possível duplicata · {score}% similaridade
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-3">
        {/* Manual side */}
        <div className="rounded-lg border border-subtle bg-surface-card-hover/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-foreground-muted mb-1">
            Sua transação
          </div>
          <div className="text-sm font-medium text-foreground line-clamp-2">
            {m?.description || "Sem descrição"}
          </div>
          <div className="text-caption text-foreground-muted mt-1">
            {_fmtDate(m?.date)} · {fmtBRL(m?.amount)}
          </div>
          {m?.category && (
            <div className="text-caption text-foreground-muted">
              Categoria: {m.category}
            </div>
          )}
        </div>

        <ArrowRight className="hidden md:block w-5 h-5 text-foreground-muted shrink-0" aria-hidden="true" />

        {/* Pluggy side */}
        <div className="rounded-lg border border-brand-green/30 bg-brand-green/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-brand-green mb-1">
            Importada do banco
          </div>
          <div className="text-sm font-medium text-foreground line-clamp-2">
            {p?.description || "Sem descrição"}
          </div>
          <div className="text-caption text-foreground-muted mt-1">
            {_fmtDate(p?.date)} · {fmtBRL(p?.amount)}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isResolving}
          onClick={() => onResolve(candidate.id, "reject_duplicate")}
          className="text-caption"
          data-testid={`reconcile-reject-${candidate.id}`}
        >
          <X className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
          Não é duplicata
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={isResolving}
          onClick={() => onResolve(candidate.id, "confirm_duplicate")}
          className="bg-brand-green text-background hover:bg-brand-green/90 font-bold text-caption"
          data-testid={`reconcile-confirm-${candidate.id}`}
        >
          <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
          Sim, é duplicata
        </Button>
      </div>
    </motion.div>
  );
}

export default function ReconciliationModal({
  open,
  onOpenChange,
  candidates = [],
  onResolve, // (candidateId, decision) => Promise
}) {
  const [resolvingId, setResolvingId] = useState(null);

  const handleResolve = async (id, decision) => {
    setResolvingId(id);
    try {
      await onResolve(id, decision);
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[80vh] overflow-y-auto"
        data-testid="reconcile-modal"
      >
        <DialogHeader>
          <DialogTitle>Confirmar duplicatas</DialogTitle>
          <DialogDescription>
            Encontramos {candidates.length}{" "}
            {candidates.length === 1 ? "transação" : "transações"} importada
            {candidates.length === 1 ? "" : "s"} do banco que parece
            {candidates.length === 1 ? "" : "m"} corresponder a algo que você já
            tinha lançado manualmente. Resolva caso a caso pra evitar contagem
            dupla nos relatórios.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {candidates.map((c) => (
            <CandidateRow
              key={c.id}
              candidate={c}
              onResolve={handleResolve}
              isResolving={resolvingId === c.id}
            />
          ))}
          {candidates.length === 0 && (
            <div className="text-center py-8 text-foreground-muted text-body">
              Sem candidatos pendentes.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
